import { useState, useCallback } from "react";
import { unwrapOpkForAdmin, wrapOpkForAdmin, bufferToBase64 } from "~/helpers/envelopeCrypto";

interface AdminWrapData {
  encrypted_opk: string;
  wrap_iv: string;
  wrap_salt: string;
}

interface WrapResult {
  encrypted_opk: string;
  wrap_iv: string;
  wrap_salt: string;
}

interface UseAdminOpkUnlockReturn {
  unlockOpk: (adminPassword: string, adminWrap: AdminWrapData) => Promise<ArrayBuffer>;
  wrapOpkWithPassword: (password: string, opkBytes: ArrayBuffer) => Promise<WrapResult>;
  isUnlocking: boolean;
  error: string | null;
  clearError: () => void;
}

/**
 * Centraliza o fluxo de desbloqueio e wrapping da OPK via senha de admin.
 * - unlockOpk: PBKDF2(senha) → AES-GCM-decrypt(OPK criptografada) → OPK em bytes
 * - wrapOpkWithPassword: PBKDF2(nova senha) → AES-GCM-encrypt(OPK) → blob criptografado
 */
export function useAdminOpkUnlock(): UseAdminOpkUnlockReturn {
  const [isUnlocking, setIsUnlocking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => setError(null), []);

  /** Deriva chave AES-256 a partir de senha via PBKDF2 (600k iterações) */
  const deriveWrappingKey = async (password: string, salt: Uint8Array) => {
    const keyMaterial = await crypto.subtle.importKey(
      "raw",
      new TextEncoder().encode(password),
      "PBKDF2",
      false,
      ["deriveKey"],
    );
    return crypto.subtle.deriveKey(
      { name: "PBKDF2", salt: salt as BufferSource, iterations: 600_000, hash: "SHA-256" },
      keyMaterial,
      { name: "AES-GCM", length: 256 },
      true,
      ["encrypt", "decrypt"],
    );
  };

  const unlockOpk = useCallback(
    async (adminPassword: string, adminWrap: AdminWrapData): Promise<ArrayBuffer> => {
      setIsUnlocking(true);
      setError(null);

      try {
        const saltBytes = Uint8Array.from(atob(adminWrap.wrap_salt), (c) => c.charCodeAt(0));
        const wrappingKey = await deriveWrappingKey(adminPassword, saltBytes);
        const rawKey = await crypto.subtle.exportKey("raw", wrappingKey);
        return await unwrapOpkForAdmin(rawKey, adminWrap.encrypted_opk, adminWrap.wrap_iv);
      } catch {
        const msg = "Senha de administrador incorreta ou dados corrompidos.";
        setError(msg);
        throw new Error(msg);
      } finally {
        setIsUnlocking(false);
      }
    },
    [],
  );

  const wrapOpkWithPassword = useCallback(
    async (password: string, opkBytes: ArrayBuffer): Promise<WrapResult> => {
      const salt = crypto.getRandomValues(new Uint8Array(16));
      const wrappingKey = await deriveWrappingKey(password, salt);
      const rawKey = await crypto.subtle.exportKey("raw", wrappingKey);
      const { encrypted_opk, wrap_iv } = await wrapOpkForAdmin(rawKey, opkBytes);
      return {
        encrypted_opk,
        wrap_iv,
        wrap_salt: bufferToBase64(salt),
      };
    },
    [],
  );

  return { unlockOpk, wrapOpkWithPassword, isUnlocking, error, clearError };
}
